import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'
import { Bilhete, BilheteStatusCode, Pedido } from "../../utils/interfaces"
import { useMutationCancelar } from '../../hooks/pagamento/mutationCancelar'
import { Loading } from '../../components/Loading'
import Swal from 'sweetalert2'
import { AxiosErrorResponse } from '../../hooks/pdv/mutationReserva'
import { PedidoStatusCode } from '../../utils/interfaces'
import { BilhetePagamento } from '../../hooks/pagamento/usePedido'
import { getUser } from '../../libs/auth'
import { formatBRL } from '../../utils/currency'
import { isMultaNaFatura } from '../../utils/multaCancelamento'

interface Props {
  isOpen: boolean
  onClose: () => void
  pedido: Pedido
  bilhetes?: BilhetePagamento
}

const bilheteStatusFinalizados = [
  BilheteStatusCode.Cancelado,
  BilheteStatusCode.Devolvido,
  BilheteStatusCode.EstornoPendente,
];

function listarBilhetes(bilhetes?: BilhetePagamento): Bilhete[] {
  return Object.values(bilhetes || {}).flatMap((grupos) =>
    (grupos || []).flatMap((grupo) =>
      (grupo.viagens || []).flatMap((viagem) => viagem.bilhetes || [])
    )
  );
}

function totalMultaPedido(bilhetes?: BilhetePagamento): number {
  return listarBilhetes(bilhetes).reduce((total, bilhete) => {
    if (bilheteStatusFinalizados.includes(bilhete.status)) {
      return total;
    }

    const multa = Number(bilhete.multa ?? bilhete.multa_cancelamento ?? 0);

    return total + (Number.isFinite(multa) ? multa : 0);
  }, 0);
}

function multaPorBilhete(bilhetes?: BilhetePagamento): number {
  const bilheteComMulta = listarBilhetes(bilhetes).find((bilhete) => {
    if (bilheteStatusFinalizados.includes(bilhete.status)) {
      return false;
    }

    const multa = Number(bilhete.multa ?? bilhete.multa_cancelamento ?? 0);

    return Number.isFinite(multa) && multa > 0;
  });

  return Number(bilheteComMulta?.multa ?? bilheteComMulta?.multa_cancelamento ?? 0);
}

function possuiBilheteGpApi(bilhetes?: BilhetePagamento): boolean {
  return listarBilhetes(bilhetes).some(
    (bilhete) => String(bilhete.adaptador ?? bilhete.adapter ?? '').toUpperCase() === 'GP-API'
  );
}

export function ModalCancelar({isOpen, onClose, pedido, bilhetes}: Props){
  const mutation = useMutationCancelar({ redirectOnSuccess: '/' })
  const user = getUser()
  const agenciaRepassaMulta = Boolean(pedido.repassar_multa ?? user?.agencia?.repassar_multa)
  const isDevolucao = [
    PedidoStatusCode.Finalizado,
    PedidoStatusCode.DevolvidoParcialmente,
  ].includes(pedido.status)
  const multaPedido = totalMultaPedido(bilhetes)
  const multaBilhete = multaPorBilhete(bilhetes)
  const pedidoTemGpApi = possuiBilheteGpApi(bilhetes)
  const multaFaturada = isDevolucao
    && multaPedido > 0
    && isMultaNaFatura(pedido, agenciaRepassaMulta)
  const deveAvisarMulta = isDevolucao
    && agenciaRepassaMulta
    && pedidoTemGpApi
    && multaPedido > 0
  const deveExibirAvisoTaxa = isDevolucao
    && agenciaRepassaMulta
    && pedidoTemGpApi

  const onConfirm = async () => {
    try {
      await mutation.mutateAsync(pedido.id)
    }catch(err: unknown){
  
      const error = err as AxiosErrorResponse

      onClose()

      const message =
        error.response?.data?.data?.message ||
        error.response?.data?.message ||
        'Nao foi possivel cancelar.';

      Swal.fire({
        icon: 'error',
        title: 'Ocorreu um erro',
        text: message,
      });
    }

  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
        {mutation.isPending && <Loading />}
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isDevolucao ? 'Devolver' : 'Cancelar'} Pedido {pedido.id}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isDevolucao ? 'Deseja continuar com a devolução?' : 'Deseja continuar com o cancelamento?'}
            {deveExibirAvisoTaxa && (
              <Alert status="warning" mt={4} borderRadius="8px">
                <AlertIcon />
                <span>
                  Esta devolução tem uma taxa de cancelamento, definido pela viação.
                  <br />
                  Após a devolução verifique a conta corrente para confirmar o valor.
                  {deveAvisarMulta && (
                    <>
                      <br />
                      Taxa de cancelamento retida por bilhete: {formatBRL(multaBilhete)}.
                    </>
                  )}
                </span>
              </Alert>
            )}
          </ModalBody>

          <ModalFooter>
            <Button colorScheme='blue' mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button variant='solid' colorScheme='red' onClick={onConfirm}>
              {deveAvisarMulta
                ? multaFaturada
                  ? `Confirmar devolução com taxa retida por bilhete de ${formatBRL(multaBilhete)}`
                  : `Confirmar devolução com taxa por bilhete de ${formatBRL(multaBilhete)}`
                : `Confirmar ${isDevolucao ? 'Devolução' : 'Cancelamento'}`}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
  )
}
